import { useState, useCallback, useEffect } from 'react';
import { showConfirm } from '@/utils/common/alert';
import { 
  IBulletinFormValues, 
  defaultBulletinFormValues, 
  IBulletin, 
  IBulletinPhoto,
  BulletinFormFieldHandler,
  BulletinFormErrors
} from '@/types/admin/bulletins';
import { composeValidators, requiredString, requireIf, validateData } from '@/utils/admin/validation';

export type EditBulletinFormErrors = BulletinFormErrors;

export const useEditBulletinForm = (initialBulletin?: IBulletin) => {
  const [formData, setFormData] = useState<IBulletinFormValues>(defaultBulletinFormValues);
  const [errors, setErrors] = useState<BulletinFormErrors>({});
  const [originalData, setOriginalData] = useState<IBulletinFormValues>(defaultBulletinFormValues);

  // Convert bulletin data to form format (matching admin-web logic)
  const convertBulletinToFormData = useCallback((bulletin: IBulletin): IBulletinFormValues => {
    return {
      title: bulletin.title || '',
      message: bulletin.description || bulletin.message || '', // Use description for display, fallback to message
      publishType: bulletin.publishType || 'draft', // Preserve original publishType
      publishDate: bulletin.publishDate || null,
      publishTime: bulletin.publishDate ? new Date(bulletin.publishDate) : null,
      highlight: Boolean(bulletin.highlight),
      sendEmail: Boolean(bulletin.sendEmail),
      pinHomepage: Boolean(bulletin.pinHomepage),
      recipientType: bulletin.recipientType || 'all',
      photos: bulletin.files?.map((file) => ({
        id: file.id, // Use actual file ID from database
        fileUrl: file.fileUrl,
        photoUrl: file.fileUrl,
        file: undefined, // No file object for existing photos (only set when uploading new files)
      })) || [],
    };
  }, []);

  // Initialize form data when bulletin is provided
  useEffect(() => {
    if (initialBulletin) {
      const formValues = convertBulletinToFormData(initialBulletin);
      setFormData(formValues);
      setOriginalData(formValues);
    }
  }, [initialBulletin, convertBulletinToFormData]);

  const validateForm = useCallback((): boolean => {
    const validator = composeValidators<IBulletinFormValues>(
      requiredString('title', 'Subject'),
      requiredString('message', 'Message'),
      requireIf(
        (data) => data.publishType === 'scheduled',
        'publishTime',
        'Publish date and time'
      ),
    );
    return validateData(formData, setErrors, validator);
  }, [formData]);

  const handleFieldChange: BulletinFormFieldHandler = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing/changing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const handlePublishTypeChange = useCallback((type: 'publishNow' | 'draft' | 'scheduled') => {
    setFormData(prev => ({
      ...prev,
      publishType: type,
      publishDate: type === 'publishNow' || type === 'draft' ? null : prev.publishDate || new Date().toISOString(),
      publishTime: type === 'publishNow' || type === 'draft' ? null : prev.publishTime || new Date(),
    }));
  }, []);

  const appendPhotos = useCallback((photosToAdd: IBulletinPhoto[]) => {
    setFormData(prev => ({
      ...prev,
      photos: [...(prev.photos || []), ...photosToAdd],
    }));
  }, []);

  const resetForm = useCallback(() => {
    if (initialBulletin) {
      const formValues = convertBulletinToFormData(initialBulletin);
      setFormData(formValues);
      setErrors({});
    } else {
      setFormData(defaultBulletinFormValues);
      setErrors({});
    }
  }, [initialBulletin, convertBulletinToFormData]);

  const showDiscardAlert = useCallback(async (onDiscard: () => void) => {
    const confirmed = await showConfirm(
      'Discard Changes',
      'Are you sure you want to discard your changes?',
      'Discard',
      'Keep Editing'
    );
    
    if (confirmed) {
      onDiscard();
    }
  }, []);

  // Check if form has changes compared to original data
  const hasChanges = useCallback(() => {
    return (
      formData.title !== originalData.title ||
      formData.message !== originalData.message ||
      formData.publishType !== originalData.publishType ||
      formData.highlight !== originalData.highlight ||
      formData.sendEmail !== originalData.sendEmail ||
      formData.pinHomepage !== originalData.pinHomepage ||
      formData.recipientType !== originalData.recipientType ||
      formData.publishDate !== originalData.publishDate ||
      formData.publishTime?.getTime() !== originalData.publishTime?.getTime() ||
      JSON.stringify(formData.photos) !== JSON.stringify(originalData.photos)
    );
  }, [formData, originalData]);

  return {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    handlePublishTypeChange,
    appendPhotos,
    resetForm,
    showDiscardAlert,
    hasChanges,
    originalData,
  };
};
